import { type ClassValue, clsx } from 'clsx';
import { isArray, isEmpty, isEqual, isNil, isPlainObject, isString, omitBy } from 'lodash-es';
import { twMerge } from 'tailwind-merge';
import { nextTick, watch } from 'vue';

import type { GroupVersionResourceList } from '@/api-client/api';
import type { GVRSLists } from '@/models/Resource';

export function getRoleCRKey(roleObj) {
  return roleObj.roleCR ? 'roleCR' : 'clusterRoleCR';
}

export function getFlattenedErrors(errors) {
  if (!errors) return null;
  if (!Array.isArray(errors)) errors = [errors];

  return errors?.reduce(function (r, e) {
    if (!e.startsWith('{')) return [e];
    let errStack = JSON.parse(e);
    while (errStack) {
      if (errStack.message) {
        // Reconcile failures have an error like "Internal error occurred: *kerror*"
        const k8sReconcileError = 'Internal error occurred: ';
        if (errStack.message.startsWith(k8sReconcileError))
          errStack.message = errStack.message?.substr(k8sReconcileError.length);
        // sometimes k8s Status errors are included which are already JSON encoded.
        if (errStack.message.startsWith('{')) errStack = JSON.parse(errStack.message);
        if (errStack.message) {
          let msg = errStack.message.trim();
          if (errStack.fields)
            msg +=
              '. ' +
              errStack.fields
                .filter((e) => typeof e.value !== 'object')
                .map((e) => `${e.name}: ${e.value}`)
                .join(', ');
          r.push(msg);
        }
      }
      errStack = errStack.cause;
    }
    return r;
  }, []);
}

export function getFreshHooks() {
  return {
    preHook: {
      enabled: false,
      blueprintSelected: '',
      actionSelected: '',
      targetSpecific: false,
      subject: { name: '', namespace: '', type: '' },
    },
    onSuccess: {
      enabled: false,
      blueprintSelected: '',
      actionSelected: '',
      targetSpecific: false,
      subject: { name: '', namespace: '', type: '' },
    },
    onFailure: {
      enabled: false,
      blueprintSelected: '',
      actionSelected: '',
      targetSpecific: false,
      subject: { name: '', namespace: '', type: '' },
    },
  };
}

export function getZeroedRetention() {
  return { hourly: 0, daily: 0, weekly: 0, monthly: 0, yearly: 0 };
}

export function getDefaultRetention() {
  return { hourly: 24, daily: 7, weekly: 4, monthly: 12, yearly: 7 };
}

export function getUsernameDisplay(username: string) {
  return username.includes('@')
    ? username.split('@')[0]
    : username
        .replace('system:serviceaccount:kasten-io:', '')
        .replace('system:serviceaccount:default:', '')
        .replace('system:serviceaccount:', '');
}

/**
 * Takes response from /resources and
 * strips out groups, versions and resources into separate arrays.
 */
export function gvrsToLists(gvrList?: GroupVersionResourceList): GVRSLists {
  const groupList: string[] = [];
  const versionList: string[] = [];
  const resourceList: string[] = [];

  for (const { Group, Version, Resource } of gvrList ?? []) {
    if (Group) groupList.push(Group);
    if (Version) versionList.push(Version);
    if (Resource) resourceList.push(Resource);
  }

  const groups = [...new Set(groupList)].sort();
  const versions = [...new Set(versionList)].sort();
  const resources = [...new Set(resourceList)].sort();

  return { groups, versions, resources };
}

export function decodeJWTUnverified(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(jsonPayload);
}

export function removeHashAndQueryString(endpoint: string) {
  const url = new URL(endpoint);
  url.search = '';
  url.hash = '';
  return url.href;
}

// Prefix a bare host (e.g. "grafana.example.com") with a scheme so the browser treats it as an
// absolute URL instead of a path relative to the K10 base URL (K10-37059). Values that are already
// absolute, protocol-relative ("//host"), or root-relative ("/path") are returned unchanged.
export function ensureAbsoluteUrl(url: string, defaultScheme = 'https'): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `${defaultScheme}://${trimmed}`;
}

export function cnMerge(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const omitEmpty = (value: Record<string, any>, defaults: Record<string, unknown> = {}) =>
  omitBy(
    value,
    (val, key) =>
      isNil(val) ||
      (isPlainObject(val) && isEmpty(val)) ||
      (isArray(val) && isEmpty(val)) ||
      (isString(val) && val === '') ||
      (key in defaults && isEqual(val, defaults[key])),
  );

export const scrollToFirstError = () => {
  nextTick(() => {
    // querySelector for the first error in the form
    const firstError = document.querySelector('.has-danger');

    if (firstError) {
      firstError.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Focus the associated input field if it exists
      const associatedInput = firstError
        .closest('.form-group')
        ?.querySelector('input, select, textarea, button');
      if (associatedInput) {
        (associatedInput as HTMLElement).focus();
      }
    }
  });
};

export const waitForReactiveValue = <T>(
  getter: () => T,
  predicate: (value: T) => boolean = (val) => val !== undefined,
): Promise<T> => {
  return new Promise((resolve) => {
    const currentValue = getter();
    if (predicate(currentValue)) {
      resolve(currentValue);
      return;
    }

    const unwatch = watch(
      getter,
      (newValue) => {
        if (predicate(newValue)) {
          unwatch();
          resolve(newValue);
        }
      },
      { immediate: true },
    );
  });
};
